import { Save, Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useDiscoveryPending, useSubmitBlocked } from '@/lib/oauth/discoveryGate';

interface Props {
  dirtyCount: number;
  isSaving: boolean;
  onSave: () => void;
  onDiscard: () => void;
  /** Number of fields failing client-side validation (#1163). Save is gated while
   *  this is > 0. Unlike the DiscoveryGate `blocked` registry, this is an
   *  aggregate, so the bar states the reason — the per-field message renders
   *  inline on the offending field. */
  invalidCount?: number;
}

export function SaveBar({ dirtyCount, isSaving, onSave, onDiscard, invalidCount = 0 }: Props) {
  const dirty = dirtyCount > 0;
  const invalid = invalidCount > 0;
  // Block Save while an OAuth-populated field is still discovering accounts, so
  // a too-fast Save can't PATCH settings with the headless value still empty
  // (#34). No-op outside a DiscoveryGateProvider (isAnyPending stays false).
  const { isAnyPending } = useDiscoveryPending();
  // Block Save while a required OAuth-populated allow-list has nothing selected
  // (#1013). Separate from the pending gate so the "Discovering accounts…"
  // spinner below stays keyed to discovery only — the reason for a block is
  // rendered inline on the offending field.
  const { isAnyBlocked } = useSubmitBlocked();
  return (
    <div
      className={[
        'mt-6 flex items-center gap-3 rounded-md border px-4 py-3',
        dirty
          ? 'border-primary/40 bg-[color-mix(in_srgb,var(--color-primary)_5%,var(--color-background))]'
          : 'border-border bg-card',
      ].join(' ')}
    >
      <div className="flex flex-1 items-center gap-3" aria-live="polite">
        {dirty ? (
          <>
            <Badge variant="default">
              <span aria-hidden className="mr-1 text-[8px]">●</span>
              {dirtyCount} unsaved change{dirtyCount === 1 ? '' : 's'}
            </Badge>
            <span className="text-sm text-muted-foreground">
              Changes apply on the next scheduled run, or trigger Run Now to apply immediately.
            </span>
          </>
        ) : (
          <span className="inline-flex items-center gap-2 text-sm text-muted-foreground">
            <Check className="h-4 w-4 text-success" aria-hidden />
            All changes saved.
          </span>
        )}
      </div>
      {invalid && (
        <span className="text-sm text-destructive" role="status">
          {invalidCount} field{invalidCount === 1 ? '' : 's'}{' '}
          {invalidCount === 1 ? 'needs' : 'need'} attention
        </span>
      )}
      {isAnyPending && (
        <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          Discovering accounts…
        </span>
      )}
      <Button variant="outline" size="sm" disabled={!dirty || isSaving} onClick={onDiscard}>
        Discard
      </Button>
      <Button
        size="sm"
        disabled={!dirty || isSaving || isAnyPending || isAnyBlocked || invalid}
        onClick={onSave}
      >
        {isSaving ? (
          <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving…</>
        ) : (
          <><Save className="mr-2 h-4 w-4" /> Save changes</>
        )}
      </Button>
    </div>
  );
}
